'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { Plus, FileText, Clock, ChevronRight, Loader2, User, Edit, CheckCircle2, XCircle, UserCircle, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ActionButton } from '@/components/buttons/action-button';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api/client';
import type { Application, ApplicationListResponse, FelixTariffItem, User as ApiUser } from '@/lib/api/types';
import { showFormError } from '@/lib/form-error';
import {
  CONNECTION_STATUS_CHIP_CLASSES,
  CONNECTION_STATUS_LABELS,
  connectionStatusesToListApiMode,
  defaultConnectionStatuses,
  defaultServiceStatuses,
} from '@/lib/request-type-status-config';
import { ConnectionServiceStatusFilter } from '@/components/filters/connection-service-status-filter';

const FELIX_ENTITY_LABELS: Record<string, string> = {
  contract: 'Договор',
  personal_account: 'Лицевой счёт',
  connection: 'Подключение',
  account: 'Учётная запись',
  portal_account: 'ЛК абонента',
  customer: 'Абонент',
  customer_document: 'Документ',
  customer_contact: 'Контакт',
};

const DASHBOARD_FELIX_STATUS_LABELS: Record<string, string> = {
  running: 'В работе',
  success: 'Успех',
  partial: 'Частично',
  failed: 'Ошибка',
  skipped: 'Пропущен',
};

export default function DashboardPage() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [felixTariffId, setFelixTariffId] = useState<string>('');
  const [createdBy, setCreatedBy] = useState<string>('');
  const [connectionStatuses, setConnectionStatuses] = useState(() => defaultConnectionStatuses());
  const [serviceStatusesUnused, setServiceStatusesUnused] = useState(() => defaultServiceStatuses());
  const [tariffs, setTariffs] = useState<FelixTariffItem[]>([]);
  const [users, setUsers] = useState<ApiUser[]>([]);

  const observerTarget = useRef<HTMLDivElement>(null);

  // Живой поиск: дебаунс 400 мс
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const loadApplications = useCallback(async (pageNum: number, append: boolean = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const connMode = connectionStatusesToListApiMode(connectionStatuses);
      if (connMode === 'EMPTY') {
        if (!append) {
          setApplications([]);
          setTotal(0);
          setPages(1);
          setHasMore(false);
        }
        return;
      }

      const statusParam =
        connMode === null || connMode === 'MULTI' ? undefined : connMode;

      const response: ApplicationListResponse = await apiClient.listApplications({
        page: pageNum,
        limit: 20,
        search: debouncedSearch || undefined,
        created_by: createdBy || undefined,
        felix_tariff_id: felixTariffId || undefined,
        status: statusParam,
      });

      let applicationsArray = Array.isArray(response.applications) ? response.applications : [];
      if (connMode === 'MULTI') {
        applicationsArray = applicationsArray.filter((a) => connectionStatuses.has(a.status));
      }

      if (append) {
        setApplications((prev) => [...prev, ...applicationsArray]);
      } else {
        setApplications(applicationsArray);
      }

      setTotal(response.total ?? 0);
      setPages(response.pages ?? 1);
      setHasMore(pageNum < (response.pages ?? 1));
    } catch (err: unknown) {
      showFormError(err instanceof Error ? err.message : 'Ошибка загрузки заявок');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch, createdBy, felixTariffId, connectionStatuses]);

  useEffect(() => {
    loadApplications(1, false);
  }, [loadApplications]);

  useEffect(() => {
    const loadTariffs = async () => {
      try {
        const res = await apiClient.getFelixTariffs();
        if (res.errno === '0' && Array.isArray(res.tariff)) {
          setTariffs(res.tariff);
        }
      } catch {
        // Игнорируем ошибку загрузки тарифов для фильтра
      }
    };
    loadTariffs();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await apiClient.getUsers({ page: 1, limit: 500 });
        setUsers(response.users ?? []);
      } catch {
        // Игнорируем ошибку загрузки пользователей для фильтра
      }
    };
    loadUsers();
  }, []);

  // Intersection Observer для автоподгрузки
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadApplications(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, loading, page, loadApplications]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#1A1A1A]">Заявки</h1>
        <Link href="/requests/create">
          <ActionButton size="sm">
            <Plus className="h-4 w-4" />
            Создать
          </ActionButton>
        </Link>
      </div>

      {/* Поиск и фильтры — одна строка, виджет статусов как на карте */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-nowrap items-center gap-2 overflow-x-auto">
            <div className="relative min-w-[200px] max-w-[280px] shrink-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A1AAB8]" />
              <Input
                id="search"
                name="search"
                placeholder="Поиск: имя, телефон, адрес…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-10 pl-9"
                aria-label="Поиск по клиенту"
              />
            </div>
            <ConnectionServiceStatusFilter
              connectionStatuses={connectionStatuses}
              serviceStatuses={serviceStatusesUnused}
              onConnectionStatusesChange={(next) => {
                setConnectionStatuses(next);
                setPage(1);
              }}
              onServiceStatusesChange={setServiceStatusesUnused}
              connectionOnly
              compact
              showTypeLabel={false}
              className="!flex-nowrap shrink-0 border-0 bg-transparent p-0"
            />
            <Select
              value={felixTariffId || 'all'}
              onValueChange={(v) => {
                setFelixTariffId(v === 'all' ? '' : v);
                setPage(1);
              }}
            >
              <SelectTrigger
                id="tariff_filter"
                name="tariff_filter"
                className="h-10 w-[min(11rem,36vw)] shrink-0"
                aria-label="Тарифный план"
              >
                <SelectValue placeholder="Все тарифы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все тарифы</SelectItem>
                {tariffs.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name ?? t.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={createdBy || 'all'}
              onValueChange={(v) => {
                setCreatedBy(v === 'all' ? '' : v);
                setPage(1);
              }}
            >
              <SelectTrigger
                id="user_filter"
                name="user_filter"
                className="h-10 w-[min(11rem,36vw)] shrink-0"
                aria-label="Сотрудник"
              >
                <SelectValue placeholder="Все сотрудники" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все сотрудники</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.full_name ?? u.email ?? u.user_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Applications List */}
      {loading && (!applications || applications.length === 0) ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !applications || applications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="mb-4 h-12 w-12 text-[#A1AAB8]" />
            <p className="mb-2 text-lg font-semibold text-[#1A1A1A]">Нет заявок</p>
            <p className="mb-4 text-sm text-[#666666]">
              Создайте первую заявку
            </p>
            <Link href="/requests/create">
              <ActionButton>
                <Plus className="h-4 w-4" />
                Создать заявку
              </ActionButton>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {(applications || []).map((application) => {
              const statusChip =
                CONNECTION_STATUS_CHIP_CLASSES[application.status] ?? 'bg-gray-100 text-gray-700';
              const statusText = CONNECTION_STATUS_LABELS[application.status] ?? application.status;

              return (
                <Link key={application.application_id} href={`/requests/${application.application_id}`}>
                  <Card className="touch-feedback transition-colors hover:bg-[#FAFAFA]">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F5F5F5]">
                          <FileText className="h-5 w-5 text-[#68707D]" />
                        </div>
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-[#1A1A1A]">
                              Заявка №{application.application_number}
                            </h3>
                            <span className={`text-xs px-2 py-0.5 rounded ${statusChip}`}>
                              {statusText}
                            </span>
                          </div>
                          
                          <div className="space-y-1 text-sm text-[#666666]">
                            <div className="flex items-center gap-4 flex-wrap">
                              <div className="flex items-center gap-1.5">
                                <Clock className="h-3.5 w-3.5" />
                                <span>Создана: {new Date(application.created_at).toLocaleDateString('ru-RU', { 
                                  day: '2-digit', 
                                  month: '2-digit', 
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}</span>
                              </div>
                              {application.created_by_name && (
                                <div className="flex items-center gap-1.5">
                                  <User className="h-3.5 w-3.5" />
                                  <span>Создал: {application.created_by_name}</span>
                                </div>
                              )}
                            </div>
                            
                            {!application.created_at || new Date(application.updated_at) > new Date(application.created_at) ? (
                              <div className="flex items-center gap-4 flex-wrap">
                                <div className="flex items-center gap-1.5">
                                  <Edit className="h-3.5 w-3.5" />
                                  <span>Обновлена: {new Date(application.updated_at).toLocaleDateString('ru-RU', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}</span>
                                </div>
                                {application.updated_by_name && (
                                  <div className="flex items-center gap-1.5">
                                    <User className="h-3.5 w-3.5" />
                                    <span>Обновил: {application.updated_by_name}</span>
                                  </div>
                                )}
                              </div>
                            ) : null}
                            
                            {/* Информация об абоненте */}
                            {application.subscriber_display_name && (
                              <div className="flex items-center gap-1.5">
                                <UserCircle className="h-3.5 w-3.5" />
                                <span className="font-medium">
                                  Абонент: {application.subscriber_display_name}
                                </span>
                              </div>
                            )}
                            
                            {/* Статус интеграции с Felix */}
                            {(application.felix_integration_status === 'full' || application.felix_integration_status === 'partial' || application.felix_integration_status === 'none') && (
                              <div className="flex flex-wrap items-center gap-2">
                                {application.felix_integration_status === 'full' && (
                                  <Badge variant="secondary" className="text-xs">
                                    Интегрирован с Felix
                                  </Badge>
                                )}
                                {application.felix_integration_status === 'partial' && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Badge variant="secondary" className="text-xs">
                                          Частично интегрирован с Felix
                                        </Badge>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="font-medium mb-1">Не интегрировано:</p>
                                        <ul className="list-disc list-inside text-xs">
                                          {(application.felix_missing_entities ?? []).map((e) => (
                                            <li key={e}>{FELIX_ENTITY_LABELS[e] ?? e}</li>
                                          ))}
                                        </ul>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {application.felix_integration_status === 'none' && (
                                  <span className="text-xs text-muted-foreground">Не интегрирован</span>
                                )}
                                {application.felix_last_run_status && (
                                  <span className="text-xs text-muted-foreground">
                                    Интеграция: {DASHBOARD_FELIX_STATUS_LABELS[application.felix_last_run_status] ?? application.felix_last_run_status}
                                  </span>
                                )}
                              </div>
                            )}
                            {/* Информация о подписях */}
                            <div className="flex items-center gap-4 flex-wrap">
                              <div className="flex items-center gap-1.5">
                                {application.abonent_signed ? (
                                  <>
                                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                                    <span className="text-green-700 font-medium">Абонент подписал</span>
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="h-3.5 w-3.5 text-red-400" />
                                    <span className="text-red-600">Абонент не подписал</span>
                                  </>
                                )}
                              </div>
                              {application.contract_signed && (
                                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-green-100">
                                  <CheckCircle2 className="h-3.5 w-3.5 text-green-700" />
                                  <span className="text-green-800 font-semibold text-xs">Договор полностью подписан</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Информация о пользователе, который вносил изменения */}
                            {application.updated_by_name && application.updated_by_name !== application.created_by_name && (
                              <div className="flex items-center gap-1.5 text-xs text-[#888888]">
                                <Edit className="h-3 w-3" />
                                <span>Изменения внес: {application.updated_by_name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 shrink-0 text-[#A1AAB8] mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          {/* Элемент для отслеживания скролла (автоподгрузка) */}
          {hasMore && (
            <div ref={observerTarget} className="h-20 flex items-center justify-center">
              {loadingMore && (
                <div className="flex items-center gap-2 text-[#666666]">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Загрузка...</span>
                </div>
              )}
            </div>
          )}

          {/* Пагинация как аварийный случай */}
          {pages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                disabled={page === 1 || loading}
                onClick={() => {
                  const prevPage = page - 1;
                  setPage(prevPage);
                  loadApplications(prevPage, false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="h-10"
              >
                Назад
              </Button>
              <span className="text-sm text-[#666666] px-4">
                Страница {page} из {pages} (Всего: {total})
              </span>
              <Button
                variant="outline"
                disabled={!hasMore || loading}
                onClick={() => {
                  const nextPage = page + 1;
                  setPage(nextPage);
                  loadApplications(nextPage, false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="h-10"
              >
                Вперед
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
